import { useState, type FC } from "react";
import { X } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Button } from "@/shared/components/ui/button";
import { US_STATES } from "@/shared/constants/constants";
import { cn } from "@/shared/utils/core";

import type { DashboardFilters } from "@/shared/types/lawyerDashboard";
import { EMPTY_FILTERS } from "@/shared/types/lawyerDashboard";
import { IconButton } from "@/shared/components/ui/icon-button";

// TODO: Add "Small estate" once deriveProceedingType supports it
const PROCEEDING_OPTIONS = ["Probate", "Administration", "Trust administration", "Probate & Trust"];
// TODO: Add "Pending intake" once deriveAuthoritySuffix supports it
const AUTHORITY_OPTIONS = ["Pre-letters", "Letters issued", "Trustee authority"];
const INVENTORY_OPTIONS = ["$500K+", "$1M+", "$5M+"];
const NEXT_STEP_OPTIONS = ["Action needed", "Elayne updates"];
const LAST_UPDATED_OPTIONS = ["Today", "Last two weeks", "Over two weeks ago"];

interface ChipProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

const Chip: FC<ChipProps> = ({ label, selected, onClick }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={cn(
      "rounded-full border px-3 py-2 text-sm transition-colors",
      selected
        ? "border-brand bg-brand-subtle font-medium text-brand"
        : "border-primary bg-white text-primary",
    )}
  >
    {label}
  </button>
);

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
}

const FilterSection: FC<FilterSectionProps> = ({ title, children }) => (
  <div className="flex flex-col gap-3">
    <p className="text-sm font-medium text-primary">{title}</p>
    {children}
  </div>
);

function toggleArrayValue(arr: string[], value: string): string[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

function toggleSingleValue(current: string | null, value: string): string | null {
  return current === value ? null : value;
}

interface FilterPanelProps {
  filters: DashboardFilters;
  onApply: (filters: DashboardFilters) => void;
  onReset: () => void;
  children: React.ReactNode;
}

export const FilterPanel: FC<FilterPanelProps> = ({ filters, onApply, onReset, children }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DashboardFilters>(filters);

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) setDraft(filters);
    setOpen(isOpen);
  };

  const handleApply = () => {
    onApply(draft);
    setOpen(false);
  };

  const handleReset = () => {
    setDraft({ ...EMPTY_FILTERS });
    onReset();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[580px] p-0 shadow-[0_0_12px_rgba(0,0,0,0.09)]"
      >
        <div className="flex max-h-[70vh] flex-col">
          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <p className="text-base font-medium text-primary">Filter by</p>
                <IconButton onClick={() => setOpen(false)} label="Close filters" tooltip={false}>
                  <X />
                </IconButton>
              </div>

              <FilterSection title="Proceeding">
                <div className="flex flex-wrap gap-3">
                  {PROCEEDING_OPTIONS.map((opt) => (
                    <Chip
                      key={opt}
                      label={opt}
                      selected={draft.proceeding.includes(opt)}
                      onClick={() =>
                        setDraft((d) => ({ ...d, proceeding: toggleArrayValue(d.proceeding, opt) }))
                      }
                    />
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="Authority">
                <div className="flex flex-wrap gap-3">
                  {AUTHORITY_OPTIONS.map((opt) => (
                    <Chip
                      key={opt}
                      label={opt}
                      selected={draft.authority.includes(opt)}
                      onClick={() =>
                        setDraft((d) => ({ ...d, authority: toggleArrayValue(d.authority, opt) }))
                      }
                    />
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="Inventory size">
                <div className="flex flex-wrap gap-3">
                  {INVENTORY_OPTIONS.map((opt) => (
                    <Chip
                      key={opt}
                      label={opt}
                      selected={draft.inventorySize === opt}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          inventorySize: toggleSingleValue(d.inventorySize, opt),
                        }))
                      }
                    />
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="Next step">
                <div className="flex flex-wrap gap-3">
                  {NEXT_STEP_OPTIONS.map((opt) => (
                    <Chip
                      key={opt}
                      label={opt}
                      selected={draft.nextStep.includes(opt)}
                      onClick={() =>
                        setDraft((d) => ({ ...d, nextStep: toggleArrayValue(d.nextStep, opt) }))
                      }
                    />
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="Location">
                <Select
                  value={draft.state ?? ""}
                  onValueChange={(v) => setDraft((d) => ({ ...d, state: v || null }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {US_STATES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterSection>

              <FilterSection title="Last updated">
                <div className="flex flex-wrap gap-3">
                  {LAST_UPDATED_OPTIONS.map((opt) => (
                    <Chip
                      key={opt}
                      label={opt}
                      selected={draft.lastUpdated === opt}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          lastUpdated: toggleSingleValue(d.lastUpdated, opt),
                        }))
                      }
                    />
                  ))}
                </div>
              </FilterSection>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-primary p-6">
            <button onClick={handleReset} className="text-sm font-medium text-primary underline">
              Reset
            </button>
            <Button variant="brand" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
